/**
 * Deciding what a fenced block in an assistant response IS, and what to show of it.
 *
 * WHY THIS EXISTS. A response that contains a class, a shell command or a config file is the point
 * of the answer, not decoration — but rendering the whole thing inline makes the conversation the
 * wrong shape: a forty-line trigger pushes the next sentence, the decision and the actions off the
 * screen. So the chat gets a preview and the workspace gets the file, and the user decides when to
 * cross over.
 *
 * NOTHING HERE FETCHES. The content is already in `part.body.text`, where the reducer accumulated
 * it from `message.part_delta`. Re-requesting a body the browser is holding would be a second
 * source of truth for the same bytes.
 *
 * Pure, because this repo has no `renderHook` and the rules that decide what a reader sees belong
 * where a node test can hold them — the same reason `transcript.ts` and `usageMeter.ts` are pure.
 */

/** How many lines of a block the chat shows before the reader opens it. */
export const PREVIEW_LINES = 8;

/**
 * Blocks shorter than this are shown in full and get no opener.
 *
 * A three-line shell command is not worth a trip to the workspace — the preview WOULD be the whole
 * thing, and an "Open" that reveals nothing new is a control that lies about having more.
 */
export const INLINE_MAX_LINES = 12;

/**
 * Languages treated as a shell transcript rather than source.
 *
 * They render without a diff gutter and with no filename guess: a shell block is a sequence of
 * commands, not a file, and inventing `script.sh` for it would name something that does not exist.
 */
const SHELL_LANGUAGES = new Set(["bash", "sh", "shell", "zsh", "console", "shell-session", "terminal"]);

/**
 * Languages worth opening in the editor at all.
 *
 * A fence with no language, or one carrying prose (`text`, `markdown`), is left as ordinary
 * markdown: the editor adds nothing to a paragraph, and an opener on every quoted line would put
 * the affordance everywhere and mean nothing.
 */
const CODE_LANGUAGES = new Set([
  "apex", "java", "typescript", "ts", "tsx", "javascript", "js", "jsx",
  "json", "yaml", "yml", "xml", "html", "css", "scss", "sql", "soql",
  "python", "py", "go", "rust", "rs", "ruby", "rb", "php", "csharp", "cs",
  "diff", "patch", "graphql", "toml", "ini", "properties",
]);

export type BlockKind = "code" | "shell" | "prose";

export function blockKind(language: string | null | undefined): BlockKind {
  const lang = (language ?? "").trim().toLowerCase();
  if (!lang) return "prose";
  if (SHELL_LANGUAGES.has(lang)) return "shell";
  return CODE_LANGUAGES.has(lang) ? "code" : "prose";
}

/**
 * The Monaco language id for a fence's tag.
 *
 * Monaco's ids are not the aliases people write in a fence, and getting it wrong costs syntax
 * highlighting silently. Anything unmapped falls through to `plaintext` rather than being guessed —
 * a wrong tokenizer is worse than none.
 */
const MONACO_LANGUAGE: Record<string, string> = {
  ts: "typescript", tsx: "typescript", js: "javascript", jsx: "javascript",
  py: "python", rb: "ruby", rs: "rust", cs: "csharp", yml: "yaml",
  sh: "shell", bash: "shell", zsh: "shell", console: "shell",
  "shell-session": "shell", terminal: "shell",
  // Apex is registered by `@ncoder/code-editor`'s `registerApexLanguage`, under this id.
  apex: "apex",
  // SOQL has no Monaco grammar; SQL is close enough to be useful and honest about being generic.
  soql: "sql",
  patch: "diff",
};

export function monacoLanguage(language: string | null | undefined): string {
  const lang = (language ?? "").trim().toLowerCase();
  if (!lang) return "plaintext";
  return MONACO_LANGUAGE[lang] ?? (CODE_LANGUAGES.has(lang) ? lang : "plaintext");
}

/**
 * The extension a fence's tag maps to, for the LABEL of an editor tab.
 *
 * WHY AN EXTENSION AT ALL. The IDE editor derives a tab's Monaco language from its label's
 * extension (`getMonacoLanguage`), so a block opened from the conversation has to carry one or it
 * is tokenised as Apex. `null` for a fence with no tag: `blockLabel` then says `.txt`, which is
 * plaintext and claims nothing.
 *
 * THIS IS NOT A FILENAME. `title` is the only thing in this module that names a real file, and only
 * when the BLOCK named it. What this builds is a label for a tab strip — it is never sent to the
 * backend, never used as a path, and never presented as a component in the org.
 */
const TAB_EXTENSION: Record<string, string> = {
  apex: "cls", java: "java", typescript: "ts", ts: "ts", tsx: "tsx",
  javascript: "js", js: "js", jsx: "jsx", json: "json", yaml: "yaml", yml: "yaml",
  xml: "xml", html: "html", css: "css", scss: "scss", sql: "sql", soql: "sql",
  python: "py", py: "py", go: "go", rust: "rs", rs: "rs", ruby: "rb", rb: "rb",
  markdown: "md", md: "md", diff: "diff", patch: "diff", toml: "toml", ini: "ini",
  bash: "sh", sh: "sh", shell: "sh", zsh: "sh", console: "sh", terminal: "sh",
};

/**
 * What a tab for this block should be called.
 *
 * The block's own name wins whenever it has one. Otherwise a generic label plus the extension that
 * carries the language — `Snippet.yaml`, and `Snippet.txt` for an untagged fence.
 */
export function blockLabel(block: Pick<CodeBlock, "title" | "language">): string {
  if (block.title) return block.title;
  const ext = TAB_EXTENSION[block.language] ?? "txt";
  return `Snippet.${ext}`;
}

/**
 * A STABLE tab id for a block, so re-opening the same snippet reuses its tab.
 *
 * `useMultiAgentEditorBridge` de-dupes an open request on `${id}:${content.length}:open`, so the id
 * has to be a function of the block itself. A counter or a random value would stack a fresh tab
 * every time a reader clicked the same block twice.
 *
 * FNV-1a over the content, which is a hash and not a checksum: it exists to keep two DIFFERENT
 * blocks in two different tabs, not to prove anything about the bytes. The label is in the id too,
 * so the same content under two names stays two tabs.
 */
export function blockTabId(block: Pick<CodeBlock, "title" | "language" | "content">): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < block.content.length; i += 1) {
    hash ^= block.content.charCodeAt(i);
    // `Math.imul` keeps this in 32-bit integer space; `*` would go through a float and lose bits.
    hash = Math.imul(hash, 0x01000193);
  }
  return `weave-block:${blockLabel(block)}:${(hash >>> 0).toString(36)}`;
}

export interface CodeBlock {
  kind: BlockKind;
  /** The fence's own tag, lowercased. Shown as the label. */
  language: string;
  /** The Monaco language id. */
  monaco: string;
  content: string;
  lineCount: number;
  /** The first `PREVIEW_LINES` lines, or the whole thing when it is short. */
  preview: string;
  /** Does opening it show the reader anything the preview did not? */
  openable: boolean;
  /**
   * A filename, ONLY when the block actually names one.
   *
   * Derived from a leading `// path/to/File.cls`-style comment or an Apex/Java type declaration —
   * both of which are the author naming the file, not this module inferring it. `null` otherwise:
   * a made-up `snippet.ts` in the chat is a technical id with extra steps.
   */
  title: string | null;
}

/** `// foo/Bar.cls`, `# infra/deploy.yml`, `<!-- x.html -->` on the first non-empty line. */
const LEADING_PATH = /^\s*(?:\/\/|#|--|<!--)\s*([\w./-]+\.[A-Za-z0-9]{1,12})\s*(?:-->)?\s*$/;
/** `public class Foo`, `trigger Foo on`, `interface Foo` — Apex/Java naming their own type. */
const DECLARED_TYPE = /\b(?:class|trigger|interface|enum)\s+([A-Za-z_]\w*)/;

const APEX_EXTENSION: Record<string, string> = { apex: ".cls", java: ".java" };

export function describeCodeBlock(language: string | null | undefined, raw: string): CodeBlock {
  const content = raw.replace(/\s+$/, "");
  const lines = content.length ? content.split("\n") : [];
  const lang = (language ?? "").trim().toLowerCase();
  const kind = blockKind(lang);

  const firstMeaningful = lines.find((line) => line.trim().length) ?? "";
  const fromPath = LEADING_PATH.exec(firstMeaningful)?.[1] ?? null;
  let title = fromPath;
  if (!title && kind === "code") {
    const declared = DECLARED_TYPE.exec(content)?.[1] ?? null;
    // `trigger Foo` in Apex is `Foo.trigger`, but the contract's own components use `.cls` for
    // classes and the distinction is not recoverable from the fence — so only name it when the
    // extension is unambiguous.
    if (declared && APEX_EXTENSION[lang]) title = `${declared}${APEX_EXTENSION[lang]}`;
  }

  const short = lines.length <= INLINE_MAX_LINES;
  return {
    kind,
    language: lang,
    monaco: monacoLanguage(lang),
    content,
    lineCount: lines.length,
    preview: short ? content : lines.slice(0, PREVIEW_LINES).join("\n"),
    /*
     * LENGTH DECIDES, NOT KIND.
     *
     * This was `kind !== "prose" && !short`, on the reasoning that "the editor adds nothing to a
     * paragraph". True of a quoted line; false of the 26-line markdown blob — headings, a table and
     * a numbered list — that a real run put in an untagged fence. It rendered in full, inside a
     * scrolling box taller than the answer around it, which is exactly the shape this module exists
     * to prevent. The fence's TAG says nothing about how much room it takes.
     *
     * A prose fence opens as `plaintext` (`monacoLanguage` returns that for an untagged fence), so
     * nothing is guessed about what it is — the reader just gets it in the workspace, at full
     * height, next to the conversation instead of inside it. Short fences are still inline: an
     * "Open" that reveals nothing is a control that lies about having more.
     */
    openable: !short,
    title,
  };
}

/**
 * The label beside the title — "apex · 42 lines", or just "13 lines".
 *
 * THE LANGUAGE IS ONLY HERE WHEN THE TITLE IS NOT IT. A block that named no file falls back to its
 * language as the title, and printing the language again produced headers reading "bash bash · 1
 * line" and accessible names reading "Open bash — bash · 13 lines". Found by looking at the
 * screenshots, not by a probe: every assertion about this string was still true.
 */
export function codeBlockLabel(block: CodeBlock): string {
  const lines = `${block.lineCount} line${block.lineCount === 1 ? "" : "s"}`;
  return block.language && block.title ? `${block.language} · ${lines}` : lines;
}

/**
 * The tag given to a fence that arrived without one.
 *
 * `plaintext` is deliberate and not arbitrary: it is one of the two untagged-ish names
 * `CHAT_LANGUAGES` already claims (`ChatCodeBlock.tsx`), so tagging with it is what makes the block
 * reachable by this app's own renderer.
 */
export const BARE_FENCE_LANGUAGE = "plaintext";

/** An opening or closing fence on this line, or null. CommonMark: ``` or ~~~, indent ≤ 3. */
function fenceRun(line: string): { char: string; length: number; info: string } | null {
  let i = 0;
  while (i < line.length && (line[i] === " " || line[i] === "\t")) i += 1;
  if (i > 3) return null;
  const char = line[i];
  if (char !== "`" && char !== "~") return null;
  let end = i;
  while (end < line.length && line[end] === char) end += 1;
  const length = end - i;
  if (length < 3) return null;
  return { char, length, info: line.slice(end).trim() };
}

/**
 * Give every untagged fence a language, so this app's own code block can render it.
 *
 * ── WHY A STRING REWRITE AND NOT A RENDERER ─────────────────────────────────────────────────────
 *
 * Streamdown resolves a custom renderer with `renderers && e && renderers.find(...)`, and the
 * `&& e` short-circuits on a falsy language — so a fence with no info string can NEVER reach a
 * `renderers` entry, whatever that entry claims. `ChatCodeBlock`'s own note records this after the
 * attempt to claim `""` failed. The language is the only key the library dispatches on, so the only
 * place to intervene is before it parses.
 *
 * ── WHAT IT FIXES, AND IT IS TWO REPORTED THINGS AT ONCE ────────────────────────────────────────
 *
 * An untagged fence fell through to streamdown's own block, which brings neither a bounded body nor
 * an `Open`: a 30-row tool result became a scroll-within-a-scroll in the middle of the conversation
 * with no way to see it properly. Its copy/download pair is a `sticky -mt-10` row drawn ABOVE the
 * box, inside a parent carrying `content-visibility: auto` — which implies paint containment, so
 * the controls were clipped along their top edge. Routed through `ChatCodeBlock` both go away
 * together: the body is capped and scrolls once, the controls sit in an in-flow header that nothing
 * clips, and a block over `OPEN_AFTER_LINES` offers `Open` into the editor that already exists.
 *
 * Only the TAG is added; not a character of the code is touched. Idempotent, and safe on a fence
 * that is still streaming — an unclosed opener is tagged too, which is exactly when the reader
 * most needs it bounded.
 *
 * No regex, per this module's house rule; fences are matched by walking the line.
 */
export function tagBareFences(markdown: string): string {
  if (!markdown.includes("```") && !markdown.includes("~~~")) return markdown;
  const lines = markdown.split("\n");
  let open: { char: string; length: number } | null = null;
  let changed = false;
  for (let i = 0; i < lines.length; i += 1) {
    const run = fenceRun(lines[i]);
    if (!run) continue;
    if (!open) {
      // CommonMark forbids a backtick in a backtick fence's info string — such a line is not a
      // fence at all, and treating it as one would swallow everything after it.
      if (run.char === "`" && run.info.includes("`")) continue;
      open = { char: run.char, length: run.length };
      if (!run.info) {
        lines[i] = `${lines[i].trimEnd()}${BARE_FENCE_LANGUAGE}`;
        changed = true;
      }
      continue;
    }
    // A closer matches the opener's character, is at least as long, and carries no info string.
    if (run.char === open.char && run.length >= open.length && !run.info) open = null;
  }
  return changed ? lines.join("\n") : markdown;
}
